// AgentBrowser.jsx
import React, { useState, useEffect } from "react"
import {
  PencilIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import AddAgentDialog from "./AddAgentDialog"
import { GetAgents, EditAgent } from "./api"

export default function AgentBrowser() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [addAgentDialog, setAddAgentDialog] = useState({
    isOpen: false,
    agent: null,
  })
  const [processing, setProcessing] = useState(false)

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item.id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedItems([])
  }

  // Handle edit agent
  const handleEditAgent = (agent) => {
    setAddAgentDialog({
      isOpen: true,
      agent: agent,
    })
  }

  // Handle toggling agent status for a single agent
  const handleToggleAgentStatus = async (agent) => {
    const newStatus = !agent.isAgent
    const action = newStatus ? "activate" : "deactivate"

    showPromptDialog({
      title: `Confirm ${newStatus ? "Activate" : "Deactivate"}`,
      message: `Are you sure you want to ${action} this agent?`,
      confirmText: newStatus ? "Activate" : "Deactivate",
      confirmVariant: newStatus ? "primary" : "error_m",
      onConfirm: async () => {
        try {
          const response = await EditAgent({
            userId: agent.id,
            isAgent: newStatus,
          })

          if (response.result) {
            showNotification({
              message: `Agent ${
                newStatus ? "activated" : "deactivated"
              } successfully`,
              className: "bg-admin_primary text-admin_text_inv",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || `Failed to ${action} agent`,
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: `An error occurred while ${action}ing agent`,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle deactivating multiple agents
  const handleDeactivateSelected = () => {
    if (selectedItems.length === 0 || processing) return

    showPromptDialog({
      title: "Confirm Deactivate",
      message: `Are you sure you want to deactivate ${selectedItems.length} ${
        selectedItems.length === 1 ? "agent" : "agents"
      }?`,
      confirmText: "Deactivate",
      confirmVariant: "error_m",
      onConfirm: async () => {
        setProcessing(true)
        try {
          let successCount = 0
          let errorCount = 0

          // Process each agent sequentially
          for (const agentId of selectedItems) {
            try {
              const response = await EditAgent({
                userId: agentId,
                isAgent: false,
              })

              if (response.result) {
                successCount++
              } else {
                errorCount++
              }
            } catch (error) {
              errorCount++
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "agent" : "agents"
              } deactivated successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-admin_text_inv",
            })
          } else if (errorCount > 0) {
            showNotification({
              message: "Failed to deactivate agents",
              className: "bg-admin_error text-white",
            })
          }

          // Reset selection and refresh
          setSelectedItems([])
          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          showNotification({
            message: "An error occurred while deactivating agents",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full flex justify-between items-end gap-2 mt-6 mb-4",
    searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    row: "h-[60px] text-admin_text text-sm xl:text-base font-normal",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item.id)) {
        return "bg-admin_canvas" // Selected row style
      }
    },
  }

  // Column definitions with selection checkbox
  const columns = [
    {
      id: "select",
      headerClassName: "w-10",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item.id)}
          onChange={(isSelected) => handleSelectItem(item.id, isSelected)}
        />
      ),
    },
    {
      id: "name",
      title: "Name",
      headerClassName: "w-[25%]",
      render: (_, item) => (
        <span className="font-medium">{item.name}</span>
      ),
    },
    {
      id: "email",
      title: "Email",
      headerClassName: "w-[30%]",
      render: (_, item) => (
        <Tooltip 
          display="block"
          content={item.email}
        >
        <div className="truncate">
          {item.email}
        </div>
        </Tooltip>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[10%]",
      render: (_, item) => (
        <span
          className={`${
            item.isAgent
              ? "text-white bg-admin_success"
              : "text-white bg-admin_error"
          } capitalize px-2 py-1 rounded-md text-xs font-medium`}
        >
          {item.isAgent ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      id: "permissions",
      title: "Permissions",
      headerClassName: "w-[20%]",
      render: (_, item) => (
        <div className="flex flex-wrap gap-1 max-w-full">
          {item.permissions && item.permissions.length > 0 ? (
            <>
              {/* Show up to 1 permissions inline */}
              {item.permissions.slice(0, 1).map((permission, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-admin_primary/10 text-admin_text_secondary rounded-full text-xs whitespace-nowrap"
                >
                  {permission}
                </span>
              ))}
              {/* If there are more permissions, show a +X indicator */}
              {item.permissions.length > 2 && (
                <span className="bg-admin_neutral_1/50 text-admin_text_secondary rounded-full text-xs whitespace-nowrap px-2 py-0.5">
                  +{item.permissions.length - 2}
                </span>
              )}
            </>
          ) : (
            <span className="text-admin_text_secondary text-xs">
              No custom permissions
            </span>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[15%] text-center",
      render: (_, item) => (
        <div className="flex justify-center gap-2">
          <Button
            variant="admin_icon"
            className="text-admin_text_secondary"
            onClick={() => handleEditAgent(item)}
            title="Edit agent"
          >
            <PencilIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="admin_icon"
            className={`${
              item.isAgent ? "text-admin_error" : "text-admin_success"
            } transition-colors`}
            onClick={() => handleToggleAgentStatus(item)}
            title={item.isAgent ? "Deactivate agent" : "Activate agent"}
          >
            {item.isAgent ? (
              <XMarkIcon className="w-5 h-5" />
            ) : (
              <PlusCircleIcon className="w-5 h-5" />
            )}
          </Button>
        </div>
      ),
    },
  ]

  {/* Table footer content - action buttons */}
  const tableFooter = selectedItems.length > 0 && (
    <div className="flex justify-end items-center mt-4 p-3 bg-admin_paper rounded">
      <Typography variant="admin_body" className="mr-4">
        {selectedItems.length}{" "}
        {selectedItems.length === 1 ? "agent" : "agents"} selected
      </Typography>

      <Button
        variant="admin_secondary"
        className=" mr-3"
        onClick={handleClearSelection}
        disabled={processing}
      >
        <div className="flex items-center space-x-1">
          <XMarkIcon className="w-5 h-5 mr-2" />
          Deselect
        </div>
      </Button>

      <Button
        variant="admin_error"
        onClick={handleDeactivateSelected}
        disabled={processing}
      >
        <div className="flex items-center space-x-1">
          <XMarkIcon className="w-5 h-5 mr-2" />
          Deactivate
        </div>
      </Button>
    </div>
  )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Support Agents
        </Typography>

        <Button
          variant="admin_primary"
          className="whitespace-nowrap flex items-center"
          onClick={() => setAddAgentDialog({ isOpen: true, agent: null })}
        >
          <PlusCircleIcon className="w-5 h-5 mr-1" /> Add Agent
        </Button>
      </div>

      {/* Main data table */}
      <DataTable
        variant="admin_browser"
        apiMethod={GetAgents}
        tableFooter={tableFooter}
        searchField="name email"
        sort={{ name: 1 }}
        localUserPreferencesKey="admin.customerSupport.agents"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="60vh"
        itemHeight="h-16"
        noData={<Typography variant="admin_body">No agents found</Typography>}
        styleOverrides={styleOverrides}
      />

      {/* Add/Edit Agent Dialog */}
      <AddAgentDialog
        isOpen={addAgentDialog.isOpen}
        agent={addAgentDialog.agent}
        onClose={() => setAddAgentDialog({ isOpen: false, agent: null })}
        onAgentAdded={() => {
          setRefreshKey((prev) => prev + 1)
        }}
      />
    </div>
  )
}
